import { translationKey } from '../../i18n';
import { localize } from '../../i18n';
import SingleLineText from '../../components/ui/SingleLineText';
import React from 'react';
import { Box, Chip, TextField } from '@mui/material';
import medicationEditChipSx from './medicationEditChipSx';

const MEDICATION_FREQUENCY_OPTIONS = [
    { value: 'DAILY', label: translationKey('매일') },
    { value: 'EVERY_OTHER_DAY', label: translationKey('격일') },
    { value: 'SPECIFIC_DAYS', label: translationKey('특정 요일') },
    { value: 'EVERY_N_DAYS', label: translationKey('며칠마다') },
    { value: 'HOURLY_INTERVAL', label: translationKey('몇 시간마다') },
    { value: 'CYCLE', label: translationKey('먹고 쉬고 반복') },
    { value: 'MONTHLY', label: translationKey('매월 특정일') },
    { value: 'AS_NEEDED', label: translationKey('필요할 때만') },
];

const MEDICATION_DAY_OPTIONS = [
    { key: 'MON', label: translationKey('월') },
    { key: 'TUE', label: translationKey('화') },
    { key: 'WED', label: translationKey('수') },
    { key: 'THU', label: translationKey('목') },
    { key: 'FRI', label: translationKey('금') },
    { key: 'SAT', label: translationKey('토') },
    { key: 'SUN', label: translationKey('일') },
];

const MEDICATION_DAY_ORDER = MEDICATION_DAY_OPTIONS.map(day => day.key);

export default function MedicationFrequencyEditor({
    value,
    onChange,
    includeAsNeeded = false,
}) {
    const form = value || {};
    const current = form.frequencyType || 'DAILY';
    const options = includeAsNeeded
        ? MEDICATION_FREQUENCY_OPTIONS
        : MEDICATION_FREQUENCY_OPTIONS.filter(option => option.value !== 'AS_NEEDED');

    const setFrequency = (nextValue) => {
        const patch = { frequencyType: nextValue };
        if (nextValue === 'AS_NEEDED') {
            patch.times = [];
        } else if (!form.times || form.times.length === 0) {
            patch.times = ['08:00'];
        }
        if (nextValue === 'SPECIFIC_DAYS' && (!form.specificDays || form.specificDays.length === 0)) patch.specificDays = ['MON'];
        if (nextValue === 'EVERY_N_DAYS' && !form.intervalDays) patch.intervalDays = 3;
        if (nextValue === 'HOURLY_INTERVAL' && !form.hourlyInterval) patch.hourlyInterval = 8;
        if (nextValue === 'CYCLE') {
            if (!form.cycleDaysOn) patch.cycleDaysOn = 3;
            if (!form.cycleDaysOff) patch.cycleDaysOff = 1;
        }
        if (nextValue === 'MONTHLY' && (!form.monthlyDays || form.monthlyDays.length === 0)) patch.monthlyDays = [1];
        onChange(patch);
    };

    const chipSx = medicationEditChipSx;
    const inputSx = {
        '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fff' },
    };
    const blockSx = {
        mt: 1.15,
        p: 1.15,
        borderRadius: 3,
        bgcolor: '#f9fafb',
        border: '1px solid #eef2f7',
    };
    const numericKeyDown = (event) => {
        if (['-', '+', 'e', 'E', '.'].includes(event.key)) event.preventDefault();
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {options.map(option => (
                    <Chip
                        key={option.value}
                        label={localize(option.label)}
                        onClick={() => setFrequency(option.value)}
                        sx={chipSx(current === option.value)}
                    />
                ))}
            </Box>

            {current === 'SPECIFIC_DAYS' && (
                <Box sx={blockSx}>
                    <SingleLineText sx={{ fontSize: '0.85rem', fontWeight: 650, color: '#555', mb: 1 }}>{localize("복용할 요일")}</SingleLineText>
                    <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap' }}>
                        {MEDICATION_DAY_OPTIONS.map(day => {
                            const selected = (form.specificDays || []).includes(day.key);
                            return (
                                <Chip
                                    key={day.key}
                                    label={localize(day.label)}
                                    onClick={() => {
                                        const currentDays = form.specificDays || [];
                                        const next = selected
                                            ? currentDays.filter(item => item !== day.key)
                                            : [...currentDays, day.key].sort((a, b) => MEDICATION_DAY_ORDER.indexOf(a) - MEDICATION_DAY_ORDER.indexOf(b));
                                        onChange({ specificDays: next.length > 0 ? next : [day.key] });
                                    }}
                                    sx={chipSx(selected)}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}

            {current === 'EVERY_N_DAYS' && (
                <TextField
                    type="number"
                    fullWidth
                    label={localize(translationKey("며칠마다"))}
                    value={form.intervalDays || ''}
                    onChange={(event) => {
                        const raw = event.target.value;
                        if (raw === '') {
                            onChange({ intervalDays: '' });
                            return;
                        }
                        onChange({ intervalDays: Math.min(Math.max(Number(raw), 2), 365) });
                    }}
                    onBlur={() => {
                        if (Number(form.intervalDays) < 2) onChange({ intervalDays: 2 });
                    }}
                    onKeyDown={numericKeyDown}
                    inputProps={{ min: 2, max: 365, inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ mt: 1.15, ...inputSx }}
                />
            )}

            {current === 'HOURLY_INTERVAL' && (
                <TextField
                    type="number"
                    fullWidth
                    label={localize(translationKey("몇 시간마다"))}
                    helperText={localize("시작일의 첫 복용 시각부터 날짜가 바뀌어도 같은 간격으로 알림을 보내요")}
                    value={form.hourlyInterval || ''}
                    onChange={(event) => {
                        const raw = event.target.value;
                        if (raw === '') {
                            onChange({ hourlyInterval: '' });
                            return;
                        }
                        onChange({ hourlyInterval: Math.min(Math.max(Number(raw), 1), 24) });
                    }}
                    onBlur={() => {
                        if (Number(form.hourlyInterval) < 1) onChange({ hourlyInterval: 1 });
                    }}
                    onKeyDown={numericKeyDown}
                    inputProps={{ min: 1, max: 24, inputMode: 'numeric', pattern: '[0-9]*' }}
                    sx={{ mt: 1.15, ...inputSx }}
                />
            )}

            {current === 'CYCLE' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, mt: 1.15 }}>
                    <TextField
                        type="number"
                        label={localize("복용 일수")}
                        value={form.cycleDaysOn || ''}
                        onChange={(event) => {
                            const raw = event.target.value;
                            if (raw === '') {
                                onChange({ cycleDaysOn: '' });
                                return;
                            }
                            onChange({ cycleDaysOn: Math.min(Math.max(Number(raw), 1), 365) });
                        }}
                        onBlur={() => {
                            if (Number(form.cycleDaysOn) < 1) onChange({ cycleDaysOn: 1 });
                        }}
                        onKeyDown={numericKeyDown}
                        inputProps={{ min: 1, max: 365, inputMode: 'numeric', pattern: '[0-9]*' }}
                        sx={inputSx}
                    />
                    <TextField
                        type="number"
                        label={localize("휴약 일수")}
                        value={form.cycleDaysOff || ''}
                        onChange={(event) => {
                            const raw = event.target.value;
                            if (raw === '') {
                                onChange({ cycleDaysOff: '' });
                                return;
                            }
                            onChange({ cycleDaysOff: Math.min(Math.max(Number(raw), 1), 365) });
                        }}
                        onBlur={() => {
                            if (Number(form.cycleDaysOff) < 1) onChange({ cycleDaysOff: 1 });
                        }}
                        onKeyDown={numericKeyDown}
                        inputProps={{ min: 1, max: 365, inputMode: 'numeric', pattern: '[0-9]*' }}
                        sx={inputSx}
                    />
                </Box>
            )}

            {current === 'MONTHLY' && (
                <Box sx={blockSx}>
                    <SingleLineText sx={{ fontSize: '0.85rem', fontWeight: 650, color: '#555', mb: 1 }}>{localize("복용할 날짜")}</SingleLineText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.55 }}>
                        {Array.from({ length: 31 }, (_, index) => index + 1).map(day => {
                            const selected = (form.monthlyDays || []).includes(day);
                            return (
                                <Box
                                    key={day}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                        const currentDays = form.monthlyDays || [];
                                        const next = selected
                                            ? currentDays.filter(item => item !== day)
                                            : [...currentDays, day].sort((a, b) => a - b);
                                        onChange({ monthlyDays: next.length > 0 ? next : [day] });
                                    }}
                                    sx={{
                                        aspectRatio: '1 / 1',
                                        borderRadius: 1.8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: selected ? '#ffe566' : '#fff',
                                        color: selected ? '#5a4a00' : '#4b5563',
                                        border: selected ? '1px solid #facc15' : '1px solid #e5e7eb',
                                        fontSize: '0.85rem',
                                        fontWeight: selected ? 700 : 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    {day}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
